import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { SiteLinkProvider } from "@quintara/site-kit";
import { RouterLink } from "./siteLink";
import { WhatsAppWidget } from "./WhatsAppWidget";

// Scroll to the hash target on navigation, otherwise jump to the top.
// The original multi-page site got this for free from the browser; a SPA
// has to reproduce it on route change.
function ScrollManager() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(decodeURIComponent(hash.slice(1)));
      if (el) {
        el.scrollIntoView();
        return;
      }
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

function Header() {
  const [open, setOpen] = useState(false);
  return (
    <header className="site-head">
      <div className="wrap">
        <Link className="brand" to="/" aria-label="Quintara home">
          <img src="/img/logo-dark.png" alt="Quintara" width={520} height={134} />
        </Link>
        <button
          className="menu-toggle"
          type="button"
          aria-expanded={open}
          aria-controls="site-nav"
          onClick={() => setOpen((o) => !o)}
        >
          {open ? "Close" : "Menu"}
        </button>
        {/* Clicking any link closes the mobile menu. */}
        <nav
          className={open ? "nav open" : "nav"}
          id="site-nav"
          aria-label="Main"
          onClick={() => setOpen(false)}
        >
          <NavLink to="/solutions">Solutions</NavLink>
          <NavLink to="/platform">Platform</NavLink>
          <NavLink to="/company">Company</NavLink>
          <Link className="btn btn-app" to="/contact">
            <span className="grad-text">Talk to us</span>
          </Link>
          {/* External link to the portal app — a plain <a>, not react-router
              <Link>, since it leaves the marketing SPA to another subdomain. */}
          <a className="btn btn-primary" href="https://app.quintaratech.com/">
            Go to app
          </a>
        </nav>
      </div>
    </header>
  );
}

function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer className="site-foot">
      <div className="wrap foot-grid">
        <div className="foot-entity">
          <img src="/img/logo-dark.png" alt="Quintara" width={520} height={134} />
          <p>Business messaging on WhatsApp for enterprises, governments, agencies, mobile operators and SMBs.</p>
          <p>
            <b style={{ color: "var(--text)", fontWeight: 600 }}>Quintara Advertising Services L.L.C</b>
            <br />
            Dubai, United Arab Emirates
            <br />
          </p>
        </div>
        <div>
          <h4>Company</h4>
          <ul>
            <li><Link to="/company">About Quintara</Link></li>
            <li><Link to="/company#consortium">The consortium</Link></li>
            <li><Link to="/careers">Careers</Link></li>
            <li><Link to="/contact">Contact</Link></li>
          </ul>
        </div>
        <div>
          <h4>Product</h4>
          <ul>
            <li><Link to="/solutions">Solutions</Link></li>
            <li><Link to="/platform">Platform</Link></li>
            <li><Link to="/platform#quality">Quality and policy</Link></li>
            <li><Link to="/platform#security">Security</Link></li>
          </ul>
        </div>
        <div>
          <h4>Legal</h4>
          <ul>
            <li><Link to="/privacy">Privacy policy</Link></li>
            <li><Link to="/terms">Terms of use</Link></li>
            <li><Link to="/government-data-request-policy">Government Data Request Policy</Link></li>
            <li><a href="https://numbase.com" target="_blank" rel="noopener">Numbase Group ↗</a></li>
            <li><a href="https://jgroup-me.com" target="_blank" rel="noopener">JGroup ↗</a></li>
          </ul>
        </div>
      </div>
      <div className="wrap foot-legal">
        <span>© {year} Quintara Advertising Services L.L.C. All rights reserved.</span>
        <span>WhatsApp and Meta are trademarks of Meta Platforms, Inc. Quintara is an independent company.</span>
      </div>
    </footer>
  );
}

export function Layout() {
  return (
    <>
      <ScrollManager />
      <Header />
      <main>
        <SiteLinkProvider value={RouterLink}>
          <Outlet />
        </SiteLinkProvider>
      </main>
      <Footer />
      <WhatsAppWidget />
    </>
  );
}
